import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Alert, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";

import { IDocument } from "../../../types/document";

import { useConfirm } from 'material-ui-confirm';
import { useListContext } from "../../../contexts/listContext";
import { useUserContext } from "../../../contexts/userContext";
import { AppConfig } from "../../../config/appConfig";
import { ReportService } from "../../../services/reportService";
import { UtilityService } from "../../../services/utilityService";

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import ReportRegistrationTable from "../../tables/reportRegistrationTable";
import Spinner from "../../spinner";
import WorkingModal, { IWorkingModalProps } from '../../workingModal';

import "../../../styles/entity/reportRegistrationList.scss";
import { ErrorProvider } from "../../../classes/errorProvider";


const ReportRegistrationList = () => {
	const confirm = useConfirm();
	const listContext = useListContext();
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<IDocument[] | undefined>(undefined);

	const [displayNameFilter, setDisplayNameFilter] = useState<string>("");
	const [nameFilter, setNameFilter] = useState<string>("");
	const [powerBiIdFilter, setPowerBiIdFilter] = useState<string>("");
	const [activeFilter, setActiveFilter] = useState<number>(1);
	const [listedFilter, setListedFilter] = useState<number>(1);

	const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	useEffect(() => {
		loadRegistrations();
	}, [])

	const loadRegistrations = async () => {
		setIsLoadingRegistrations(true);
		setErrorMessage(undefined);

		ReportService.getReportRegistrations()
			.then((response) => {
				console.log('ReportService.getReportRegistrations: SUCCESS', response);
				const reportRegistrations = ReportService.mapReportRegistrations(response.data);
				setGridView(reportRegistrations);

				// Update the list context with the new report registrations
				listContext.updateListDictionary({...listContext, reportRegistrations: [...reportRegistrations].sort((a, b) => UtilityService.compareNumbers(a.ordinal, b.ordinal))});				
			})
			.catch((error) => {
				console.log('ReportService.getReportRegistrations: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setIsLoadingRegistrations(false);
			});
	}

	const handleDelete = async (id: number) => {
		if (await
			UtilityService.ConfirmationModal(
				confirm,
				"Delete Report Registration",
				<>
					<p>
						Unregistering this report will remove it from the Reports menu and make it unavailble anywhere it is embedded. The report itself in Power BI will not be affected.<br />
						<br />
						Would you like to continue?
					</p>
				</>,
				{ confirmationText: "Continue", confirmationButtonProps: { variant: 'contained' }, cancellationButtonProps: { color: 'error', variant: 'contained' } }
			) == false) {
			return;
		}
		console.log('confirmed delete');

		setWorkingModalParams({ params: { content: "Unregistering report..." } });
		ReportService.unregister(id)
			.then((response) => {
				console.log('ReportService.unregister: SUCCESS', response);
				loadRegistrations();
			})
			.catch((error) => {
				console.log('ReportService.unregister: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	const handleOrdinalChange = (id: number, ordinal: number) => {
		setWorkingModalParams({ params: { content: "Updating report position..." } });
		ReportService.changeOrdinal(id, ordinal)
			.then((response) => {
				console.log('ReportService.updateReportOrdinal: SUCCESS', response);
				loadRegistrations();
			})
			.catch((error) => {
				console.log('ReportService.updateReportOrdinal: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	return (
		<div className="hwi-report-registration-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageReportRegistrations}>
					<section className={`hwi-entity-search-bar`}>
						<div className="search-header">
							<div className="title">Registered Reports</div>
							<div><NavLink to="/report/registration/register"><Button variant="outlined">New Report</Button></NavLink></div>
						</div>
						<section className="search-options">
							<FormControl size="small">
								<TextField
									label="Display Name"
									name="displayName"
									value={displayNameFilter}
									size="small"
									onChange={(event) => { setDisplayNameFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<TextField
									label="Name"
									name="name"
									value={nameFilter}
									size="small"
									onChange={(event) => { setNameFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<TextField
									label="Power BI ID"
									name="powerBiId"
									value={powerBiIdFilter}
									size="small"
									onChange={(event) => { setPowerBiIdFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Active</InputLabel>
								<Select
									value={activeFilter}
									label="Active"
									onChange={(event) => { setActiveFilter(+event.target.value) }}
								>
									<MenuItem value={-1} key={-1}>All</MenuItem>
									<MenuItem value={1} key={1}>Active</MenuItem>
									<MenuItem value={0} key={0}>Inactive</MenuItem>
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Listed</InputLabel>
								<Select
									value={listedFilter}
									label="Listed"
									onChange={(event) => { setListedFilter(+event.target.value) }}
								>
									<MenuItem value={-1} key={-1}>All</MenuItem>
									<MenuItem value={1} key={1}>Listed</MenuItem>
									<MenuItem value={0} key={0}>Unlisted</MenuItem>
								</Select>
							</FormControl>
						</section>
					</section>
					<section className={`results`}>
						<Spinner dependency={isLoadingRegistrations} text="Loading Report Registrations...">
							{errorMessage &&
								<Alert className="alert-error"
									severity="error"
									action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
									{errorMessage}
								</Alert>
							}
							<ReportRegistrationTable
								data={gridView}
								displayName={displayNameFilter}
								name={nameFilter}
								powerBiId={powerBiIdFilter}
								active={activeFilter}
								listed={listedFilter}
								onUnregisterRequest={(id) => handleDelete(id)}
								onOrdinalChangeRequest={(id, ordinal) => handleOrdinalChange(id, ordinal)}
							></ReportRegistrationTable>
						</Spinner>
					</section>
					<WorkingModal params={workingModalParams?.params}></WorkingModal>
				</PermissionGate>
			</Container>
		</div>
	);
};

export default ReportRegistrationList;